import React, {
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  Fragment,
  useRef,
  type FC,
  type ComponentProps,
} from 'react';

import { faPlusSquare, faMinusSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import axios from 'axios';
import { toast } from 'sonner';

import { Currency } from '@shared/components/helpers';
import { Pagination } from '@shared/components/pagination/pagination';
import { IPagination } from '@shared/types/pagination';
import { UserRole } from '@shared/types/user_role';

import { SuspendButton as AccountSuspendButton } from '@admin/components/account/suspend_button';
import { Action as BillingReceiptAction } from '@admin/components/billing/receipts/action';
import { Action as BillingRefundAction } from '@admin/components/billing/refunds/action';
import { SourceIcon } from '@admin/components/billing/sources/source_icon';
import { Roles } from '@admin/components/helpers/roles';
import { Titleize, titleize } from '@admin/components/helpers/titleize';
import { InvoiceSummary } from '@admin/components/invoice_summary';
import { Billing__Source } from '@admin/schema';

import { BillingDateChangeModal } from './billing_date_change_modal';
import { InvoiceRowActions } from './invoice_row_actions';
import type { IFullInvoice, ITransaction } from './types';

export type { IFullInvoice } from './types';

interface IStripeSummary {
  credit_balance: number | string;
  upcoming_charges: number | string;
  upcoming_charge_at: string | null;
  delinquent_balance: number | string;
}

const REFRESH_EVENT = 'admin:account-invoices-refresh';

/** REST `paginated` helper shape (snake_case) from `/accounts/:id/invoices.json`. */
interface IJBuilderPagination {
  current_page: number;
  next_page?: number;
  prev_page?: number;
  total_pages: number;
  total_count: number;
}

const paginationFromJbuilder = (p: IJBuilderPagination): IPagination => ({
  currentPage: p.current_page,
  nextPage: p.next_page ?? null,
  prevPage: p.prev_page ?? null,
  totalPages: p.total_pages,
  totalCount: p.total_count,
});

const invoiceStatusLabelClass = (state: string): string => {
  switch (state) {
    case 'refunded':
      return 'label-primary';
    case 'paid':
      return 'label-success';
    case 'failed':
      return 'label-warning';
    case 'final attempt failed':
      return 'label-danger';
    case 'not attempted':
      return 'label-info';
    default:
      return 'label-default';
  }
};

const latestRefundDate = (invoice: IFullInvoice): string | null => {
  if (!invoice.refunds?.length) return null;
  const times = invoice.refunds.map((r) => new Date(r.date).getTime()).filter((t) => Number.isFinite(t));
  if (times.length === 0) return null;
  return new Date(Math.max(...times)).toISOString();
};

const transactionHistory = (invoice: IFullInvoice): ITransaction[] => {
  return [...invoice.charges, ...invoice.offline_payments, ...invoice.refunds].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  );
};

const refundableTx = (transaction: ITransaction): boolean =>
  transaction.type === 'charge' && transaction.status === 'succeeded' && transaction.refunded === false;

const dayOptions = (subscriptionBillingDay: number): number[] => {
  const out: number[] = [];
  for (let i = 1; i <= 28; i++) {
    if (i !== subscriptionBillingDay) out.push(i);
  }
  return out;
};

/** Map REST invoice `source` JSON (snake_case fields) to SourceIcon props. */
const sourceForChargeIcon = (
  raw: unknown,
): Pick<Billing__Source, 'id' | 'brand' | 'default' | 'fundingType' | 'number'> => {
  const s = raw as {
    id?: string | number;
    brand?: string;
    default?: boolean;
    funding_type?: string;
    fundingType?: string;
    number?: string;
  };
  return {
    id: String(s.id ?? ''),
    brand: s.brand ?? '',
    default: Boolean(s.default),
    fundingType: (s.funding_type ?? s.fundingType) as Billing__Source['fundingType'],
    number: s.number ?? '',
  };
};

const isRefundedLikeStatus = (status: string): boolean => {
  const normalized = status.toLowerCase();
  return normalized === 'refunded' || normalized === 'partially refunded';
};

const invoiceCurrencyCode = (currency: string): 'USD' | 'CAD' => {
  const upper = (currency || 'usd').toUpperCase();
  return upper === 'CAD' ? 'CAD' : 'USD';
};

export interface AccountInvoicesPanelProps {
  accountID: string;
  billingDateChangeable: boolean;
  billingDateChangedOn: string | null;
  subscriptionBillingDay: number;
  suspendable: boolean;
  onSuspended(): void;
  onShowChargesNew(): void;
  onShowChargesNewPrepayment(): void;
  footerActionsVisible?: boolean;
}

export const AccountInvoicesPanel: FC<AccountInvoicesPanelProps> = ({
  accountID,
  billingDateChangeable,
  billingDateChangedOn,
  subscriptionBillingDay,
  suspendable,
  onSuspended,
  onShowChargesNew,
  onShowChargesNewPrepayment,
  footerActionsVisible = true,
}) => {
  const [stripe, setStripe] = useState<IStripeSummary | null>(null);
  const [invoices, setInvoices] = useState<IFullInvoice[]>([]);
  const [pagination, setPagination] = useState<IPagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState<Record<number, boolean>>({});
  const [billingModalOpen, setBillingModalOpen] = useState(false);
  /** Incremented per refresh so overlapping async loads cannot apply stale results. */
  const requestSeqRef = useRef(0);
  const panelRootRef = useRef<HTMLDivElement>(null);
  /** Set only when the user changes invoice page via pagination (not initial load or invoice actions). */
  const pendingInvoicePaginationScrollRef = useRef(false);

  const loadStripe = useCallback(async (): Promise<IStripeSummary> => {
    const { data } = await axios.get<IStripeSummary>(`/accounts/${accountID}/stripes.json`);
    return data;
  }, [accountID]);

  const loadInvoices = useCallback(async (): Promise<{
    results: IFullInvoice[];
    pagination: IPagination;
  }> => {
    const { data } = await axios.get<{ results: IFullInvoice[]; pagination: IJBuilderPagination }>(
      `/accounts/${accountID}/invoices.json`,
      { params: { page } },
    );
    return { results: data.results, pagination: paginationFromJbuilder(data.pagination) };
  }, [accountID, page]);

  const refresh = useCallback(async () => {
    const seq = ++requestSeqRef.current;
    setLoading(true);
    try {
      // Stripe summary and invoice list are independent: the panel used Promise.all so a
      // failing /stripes.json (e.g. Stripe API error) incorrectly hid invoices and showed
      // a generic invoices error. Load each outcome separately.
      const [stripeOutcome, invoicesOutcome] = await Promise.allSettled([loadStripe(), loadInvoices()]);
      if (seq !== requestSeqRef.current) return;

      if (stripeOutcome.status === 'fulfilled') {
        setStripe(stripeOutcome.value);
      } else {
        console.error('Account stripe summary failed', stripeOutcome.reason);
        setStripe(null);
      }

      if (invoicesOutcome.status === 'fulfilled') {
        setInvoices(invoicesOutcome.value.results);
        setPagination(invoicesOutcome.value.pagination);
      } else {
        console.error('Account invoices load failed', invoicesOutcome.reason);
        toast.error('Unable to load invoices. Please refresh the page.');
      }
    } finally {
      if (seq === requestSeqRef.current) {
        setLoading(false);
      }
    }
  }, [loadInvoices, loadStripe]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    const onRefresh = () => {
      void refresh();
    };
    document.addEventListener(REFRESH_EVENT, onRefresh);
    return () => document.removeEventListener(REFRESH_EVENT, onRefresh);
  }, [refresh]);

  useLayoutEffect(() => {
    if (!loading && pendingInvoicePaginationScrollRef.current) {
      pendingInvoicePaginationScrollRef.current = false;
      panelRootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
    }
  }, [loading]);

  const onInvoiceActionSave: ComponentProps<typeof InvoiceRowActions>['onInvoiceActionSave'] = (
    _attemptID,
    message,
    refetchAll,
  ) => {
    toast.info(message);
    if (refetchAll) {
      document.dispatchEvent(new CustomEvent('admin:accounts-show-refresh'));
    } else {
      void refresh();
    }
  };

  const toggleOpen = (id: number) => {
    setOpenIds((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const formatMedium = (d: string | null) => (d ? moment(d).format('lll') : '');

  const delinquent = stripe ? Number(stripe.delinquent_balance) : 0;
  const upcoming = stripe ? Number(stripe.upcoming_charges) : 0;

  return (
    <div ref={panelRootRef} className="panel account-invoices-panel">
      <div id="charge-anchor" className="panel-heading">
        <h3 className="panel-title">Invoices</h3>
      </div>
      <div className="row">
        <div className="col-xs-4">
          <div className="panel-colorful">
            <div className="panel-body text-center">
              {delinquent > 0 ? (
                <Fragment>
                  <div className="text-attention text-danger">
                    Delinquent Amount: <Currency value={stripe?.delinquent_balance ?? 0} precision={2} />
                  </div>
                  <div>
                    <AccountSuspendButton
                      accountID={String(accountID)}
                      suspendable={suspendable}
                      onSuspended={onSuspended}
                    />
                  </div>
                </Fragment>
              ) : (
                <div>
                  Delinquent Amount: <Currency value={stripe?.delinquent_balance ?? 0} precision={2} />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="col-xs-4">
          <div className="panel-colorful">
            <div className="panel-body text-center">
              {upcoming > 0 ? (
                <span className="text-attention text-mint">
                  Due on {formatMedium(stripe?.upcoming_charge_at ?? null)}:{' '}
                  <Currency value={stripe?.upcoming_charges ?? 0} precision={2} />
                </span>
              ) : (
                <span>{stripe?.upcoming_charge_at ?? ''}</span>
              )}
              {billingDateChangeable && (
                <Roles show={[UserRole.Admin, UserRole.Billing, UserRole.Retention, UserRole.L2Agent]}>
                  <span style={{ marginLeft: 8 }}>
                    <button type="button" className="btn btn-primary" onClick={() => setBillingModalOpen(true)}>
                      Change Billing Date
                    </button>
                  </span>
                </Roles>
              )}
            </div>
          </div>
        </div>
        <div className="col-xs-4">
          <div className="panel-colorful">
            <div
              className={`panel-body text-center${
                Number(stripe?.credit_balance) > 0 ? ' text-attention text-mint' : ''
              }`}
            >
              Credits: <Currency value={stripe?.credit_balance ?? 0} precision={2} />
            </div>
          </div>
        </div>
      </div>

      <div className="panel-body">
        <table className="table table-condensed">
          <thead>
            <tr>
              <th className="text-left col-md-1 col-xs-1">Invoice</th>
              <th className="text-left col-md-1 col-xs-2">Date</th>
              <th className="text-left col-md-3 col-xs-4">Description</th>
              <th className="text-left col-md-2 hidden-xs">Note</th>
              <th className="col-md-2 col-xs-2" />
              <th className="col-md-1 col-xs-1">Payment Status</th>
              <th className="text-right col-md-1 col-xs-1">Total</th>
              <th className="text-right col-md-1 col-xs-1">Due</th>
              <th className="text-right col-zero" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={9} className="text-center text-muted">
                  Loading…
                </td>
              </tr>
            )}
            {!loading &&
              invoices.map((invoice) => {
                const open = !!openIds[invoice.id];
                const lastCharge = invoice.charges.length > 0 ? invoice.charges[invoice.charges.length - 1] : undefined;
                const refundShowDate = isRefundedLikeStatus(invoice.status) ? latestRefundDate(invoice) : null;
                return (
                  <Fragment key={invoice.id}>
                    <tr>
                      <td className="text-left col-md-1 col-xs-1 link">
                        <div
                          role="button"
                          tabIndex={0}
                          aria-expanded={open}
                          aria-label={open ? 'Collapse invoice details' : 'Expand invoice details'}
                          onClick={() => toggleOpen(invoice.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleOpen(invoice.id);
                            }
                          }}
                        >
                          <FontAwesomeIcon
                            id={`accordian-invoice-icon-${invoice.id}`}
                            icon={open ? faMinusSquare : faPlusSquare}
                          />
                          {invoice.id} <span className="stripe-id">({invoice.stripe_id})</span>
                        </div>
                      </td>
                      <td className="text-left col-md-1 col-xs-2">{moment(invoice.date).format('L')}</td>
                      <td className="text-left col-md-3 col-xs-4">
                        <InvoiceSummary
                          invoice={{
                            summary: invoice.summary,
                            tax: Number(invoice.tax),
                            pretax_subtotal: Number(invoice.pretax_subtotal),
                          }}
                        />
                        {invoice.attempted && !invoice.paid_at && lastCharge && lastCharge.failure_message && (
                          <div className="text-danger"> {lastCharge.failure_message}</div>
                        )}
                      </td>
                      <td className="text-left col-md-2 hidden-xs">
                        {invoice.notes.map((note) => (
                          <div key={note.id != null ? String(note.id) : `${note.body}-${note.user.name}`}>
                            {note.body} <b>{note.user.name}</b>
                          </div>
                        ))}
                      </td>
                      <td className="col-md-2 col-xs-1">
                        <InvoiceRowActions
                          invoice={invoice}
                          onExpand={() => setOpenIds((p) => ({ ...p, [invoice.id]: true }))}
                          onInvoiceActionSave={onInvoiceActionSave}
                        />
                      </td>
                      <td className="col-md-1 col-xs-1">
                        <div className="mar-top mar-btm">
                          <span className={`label ${invoiceStatusLabelClass(invoice.status)}`}>
                            <Titleize text={invoice.status} />
                          </span>
                          <br />
                          {invoice.status === 'paid' && invoice.paid_at && (
                            <span className="label-forgiven">on {moment(invoice.paid_at).format('ll')}</span>
                          )}
                          {refundShowDate && (
                            <span className="label-forgiven">on {moment(refundShowDate).format('ll')}</span>
                          )}
                          {invoice.next_payment_attempt_at && (
                            <span className="label-forgiven">
                              Next Attempt: {moment(invoice.next_payment_attempt_at).format('ll')}
                            </span>
                          )}
                          {invoice.prepayment_invoice_id && (
                            <span className="label-forgiven">Prepaid by invoice #{invoice.prepayment_invoice_id}</span>
                          )}
                          {invoice.forgiven && <span className="label label-primary label-forgiven">Forgiven</span>}
                        </div>
                      </td>
                      <td className="text-right col-md-1 col-xs-1">
                        <strong>
                          <Currency value={invoice.total} currency={invoiceCurrencyCode(invoice.currency)} />
                        </strong>
                      </td>
                      <td className="text-right col-md-1 col-xs-1">
                        <strong className={Number(invoice.amount_owed) > 0 ? 'text-danger' : undefined}>
                          <Currency value={invoice.amount_owed} currency={invoiceCurrencyCode(invoice.currency)} />
                        </strong>
                      </td>
                      <td className="text-right col-zero">
                        {(invoice.status !== 'partially paid' || invoice.original_id === invoice.id) &&
                          invoice.is_stripe_invoice && (
                            <a
                              href={`/stripe/invoices/${invoice.id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-default"
                            >
                              View
                            </a>
                          )}
                      </td>
                    </tr>
                    <tr className="accordian-tr">
                      <td colSpan={9} className="nuke-padding">
                        {open && (
                          <div className="accordian-div">
                            <div className="col-md-12 text-left">
                              {(invoice.charges.length > 0 || invoice.offline_payments.length > 0) && (
                                <table className="table table-striped">
                                  <thead>
                                    <tr>
                                      <th className="col-sm-2">Type</th>
                                      <th className="col-sm-2">Date</th>
                                      <th className="col-sm-1">Amount</th>
                                      <th className="col-sm-1" />
                                      <th className="col-sm-3">Status</th>
                                      <th className="col-sm-3">Method</th>
                                      <th className="col-zero" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {transactionHistory(invoice).map((transaction) => (
                                      <tr key={`${transaction.type}-${transaction.id}`}>
                                        <td className="col-sm-2">{titleize(transaction.type.replace(/_/g, ' '))}</td>
                                        <td className="col-sm-2">{moment(transaction.date).format('ll')}</td>
                                        <td className="col-sm-1">
                                          <Currency
                                            value={transaction.amount}
                                            currency={invoiceCurrencyCode(invoice.currency)}
                                          />
                                        </td>
                                        <td className="col-sm-1">
                                          {refundableTx(transaction) && (
                                            <BillingRefundAction
                                              refundable={{
                                                id: String(transaction.id),
                                                amount: transaction.amount,
                                              }}
                                            />
                                          )}
                                        </td>
                                        <td className="col-sm-3">{titleize(transaction.status.replace(/_/g, ' '))}</td>
                                        <td className="col-sm-3">
                                          {transaction.type === 'charge' && transaction.source && (
                                            <SourceIcon
                                              source={sourceForChargeIcon(transaction.source)}
                                              extraDetails
                                              scale={2}
                                            />
                                          )}
                                          {transaction.type === 'offline_payment' && (
                                            <span>
                                              {titleize(String(transaction.kind ?? '').replace(/_/g, ' '))}{' '}
                                              {transaction.transaction_number}
                                            </span>
                                          )}
                                        </td>
                                        <td className="col-zero col-nowrap">
                                          {transaction.status !== 'failed' && (
                                            <Fragment>
                                              <BillingReceiptAction
                                                transaction={{
                                                  id: String(transaction.id),
                                                  type: transaction.type as 'charge' | 'refund' | 'offline_payment',
                                                  amount: transaction.amount,
                                                  date: transaction.date,
                                                }}
                                              />
                                              {transaction.type === 'charge' && (
                                                <a
                                                  target="_blank"
                                                  href={`/stripe/charges/${transaction.id}`}
                                                  className="btn btn-default"
                                                  rel="noreferrer"
                                                >
                                                  View
                                                </a>
                                              )}
                                            </Fragment>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
          </tbody>
        </table>
        {pagination && (
          <Pagination
            pagination={pagination}
            onPage={(p) => {
              pendingInvoicePaginationScrollRef.current = true;
              panelRootRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
              setPage(p);
            }}
          />
        )}
      </div>

      {footerActionsVisible && (
        <div className="panel-footer">
          <div className="text-right">
            {delinquent > 0 && (
              <a className="btn btn-success" href={`/accounts/${accountID}/payment_plans/new?type=LUMP_SUM`}>
                Make Payment
              </a>
            )}{' '}
            <Roles
              show={[
                UserRole.Admin,
                UserRole.Manager,
                UserRole.Teamlead,
                UserRole.Retention,
                UserRole.L2Agent,
                UserRole.Billing,
                UserRole.BillingAdmin,
              ]}
            >
              <button type="button" className="btn btn-primary" onClick={onShowChargesNewPrepayment}>
                Prepay
              </button>{' '}
              <button type="button" className="btn btn-primary" onClick={onShowChargesNew}>
                New Invoice
              </button>
            </Roles>
          </div>
        </div>
      )}

      {billingModalOpen && (
        <BillingDateChangeModal
          accountID={accountID}
          billingDateChangedOn={billingDateChangedOn}
          subscriptionBillingDay={subscriptionBillingDay}
          dayOptions={dayOptions(subscriptionBillingDay)}
          onClose={() => setBillingModalOpen(false)}
          onSuccess={() => {
            setBillingModalOpen(false);
            toast.info('Billing Date Successfully Changed!');
            document.dispatchEvent(new CustomEvent('admin:accounts-show-refresh'));
          }}
        />
      )}
    </div>
  );
};
